import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Megaphone, Percent, Users, Zap, Trash2, Edit2, ImageIcon } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { Badge, EmptyState, Input, Spinner } from '@terra/ui';

import { api } from '../lib/api';

// ══════════════════════════════════════════════════════════════════
// MARKETING
// ══════════════════════════════════════════════════════════════════

// ── Shared field helpers ──────────────────────────────────────────

function FieldLabel({ children }: { children: ReactNode }) {
  return <label className="text-ink mb-1 block text-[13px] font-bold">{children}</label>;
}

function FormSelect({
  name,
  defaultValue,
  children,
}: {
  name: string;
  defaultValue?: string;
  children: ReactNode;
}) {
  return (
    <select
      name={name}
      defaultValue={defaultValue}
      className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 h-10 w-full rounded-xl border px-3 text-[13px] font-medium outline-none transition-colors focus:ring-2"
    >
      {children}
    </select>
  );
}

// ── Dynamic Pop-up ────────────────────────────────────────────────

export function DynamicPopupPage() {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editingPopup, setEditingPopup] = useState<any>(null);

  const { data: popupsResponse, isLoading } = useQuery({
    queryKey: ['dynamic-popups'],
    queryFn: () => api.marketing.listPopups(),
  });

  const popups = popupsResponse || [];

  const createMutation = useMutation({
    mutationFn: (data: any) => api.marketing.createPopup(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dynamic-popups'] });
      setShowAdd(false);
      setEditingPopup(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.marketing.deletePopup(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dynamic-popups'] });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      title: formData.get('title') as string,
      imageUrl: (formData.get('imageUrl') as string) || null,
      linkUrl: (formData.get('linkUrl') as string) || null,
      active: formData.get('active') === 'on',
      location: formData.get('location') as string,
      delaySeconds: parseInt(formData.get('delaySeconds') as string) || 0,
      startsAt: formData.get('startsAt')
        ? new Date(formData.get('startsAt') as string).toISOString()
        : null,
      endsAt: formData.get('endsAt')
        ? new Date(formData.get('endsAt') as string).toISOString()
        : null,
    };
    createMutation.mutate(data);
  };

  return (
    <div className="space-y-6 pb-8">
      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm"
          >
            <div className="border-border/40 mb-5 flex items-center gap-2 border-b pb-4">
              <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
                <ImageIcon className="h-4 w-4" />
              </div>
              <h3 className="text-ink text-[15px] font-extrabold">
                {editingPopup ? 'Edit Popup' : 'New Popup'}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <FieldLabel>Popup Title</FieldLabel>
                <Input
                  name="title"
                  required
                  placeholder="e.g. Summer Blowout Sale"
                  defaultValue={editingPopup?.title}
                />
              </div>
              <div>
                <FieldLabel>Location</FieldLabel>
                <FormSelect name="location" defaultValue={editingPopup?.location || 'center'}>
                  <option value="center">Center</option>
                  <option value="bottom-left">Bottom Left</option>
                  <option value="bottom-right">Bottom Right</option>
                  <option value="top-left">Top Left</option>
                  <option value="top-right">Top Right</option>
                </FormSelect>
              </div>
              <div>
                <FieldLabel>Image URL</FieldLabel>
                <Input
                  name="imageUrl"
                  placeholder="https://..."
                  defaultValue={editingPopup?.imageUrl}
                />
              </div>
              <div>
                <FieldLabel>Link URL</FieldLabel>
                <Input
                  name="linkUrl"
                  placeholder="https://..."
                  defaultValue={editingPopup?.linkUrl}
                />
              </div>
              <div>
                <FieldLabel>Delay (seconds)</FieldLabel>
                <Input
                  name="delaySeconds"
                  type="number"
                  min="0"
                  required
                  defaultValue={editingPopup?.delaySeconds || 0}
                />
              </div>
              <div>
                <FieldLabel>Starts At (optional)</FieldLabel>
                <Input
                  name="startsAt"
                  type="datetime-local"
                  defaultValue={editingPopup?.startsAt?.slice(0, 16)}
                />
              </div>
              <div>
                <FieldLabel>Ends At (optional)</FieldLabel>
                <Input
                  name="endsAt"
                  type="datetime-local"
                  defaultValue={editingPopup?.endsAt?.slice(0, 16)}
                />
              </div>
              <div className="flex flex-col justify-between gap-3">
                <label className="mt-5 flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    name="active"
                    defaultChecked={editingPopup ? editingPopup.active : true}
                    className="border-border/60 rounded"
                  />
                  <span className="text-ink text-[13px] font-bold">Active</span>
                </label>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="bg-ink hover:bg-ink/90 flex h-10 w-full items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Saving...' : 'Save Popup'}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Popup List */}
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
              <ImageIcon className="h-4 w-4" strokeWidth={2} />
            </div>
            <span className="text-ink text-[14px] font-bold">All Dynamic Popups</span>
          </div>
          <button
            onClick={() => {
              setEditingPopup(null);
              setShowAdd((v) => !v);
            }}
            className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            {showAdd ? 'Close Form' : '+ Create Popup'}
          </button>
        </div>
        <div className="p-0">
          {isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : popups.length === 0 ? (
            <div className="border-border/60 bg-surface-subtle/30 m-5 rounded-2xl border border-dashed py-12">
              <EmptyState
                icon={<ImageIcon className="text-ink-muted h-10 w-10" />}
                title="No popups yet"
                description="No dynamic popups have been created yet."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-16 py-3 pl-5 pr-4">#</th>
                    <th className="py-3 pr-4">Title</th>
                    <th className="py-3 pr-4">Location</th>
                    <th className="py-3 pr-4 text-center">Delay</th>
                    <th className="py-3 pr-4 text-center">Status</th>
                    <th className="py-3 pr-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {popups.map((popup: any, idx: number) => (
                    <tr key={popup.id} className="hover:bg-surface-subtle/30 transition-colors">
                      <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                        {(idx + 1).toString().padStart(2, '0')}
                      </td>
                      <td className="text-ink py-3 pr-4 font-semibold">{popup.title}</td>
                      <td className="text-ink-muted py-3 pr-4 font-medium capitalize">
                        {popup.location}
                      </td>
                      <td className="py-3 pr-4 text-center">
                        <span className="bg-surface-subtle text-ink-muted border-border/40 inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-[12px] font-bold">
                          {popup.delaySeconds}s
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-center">
                        <Badge tone={popup.active ? 'success' : 'neutral'}>
                          {popup.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-3 pr-5">
                        <div className="flex items-center justify-end">
                          <button
                            onClick={() => {
                              if (window.confirm(`Delete popup "${popup.title}"?`))
                                deleteMutation.mutate(popup.id);
                            }}
                            className="text-ink-muted flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-[18px] w-[18px]" strokeWidth={2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Custom Alert ──────────────────────────────────────────────────

type AlertCorner = 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';

const ALERT_LOCATIONS: { value: AlertCorner; label: string }[] = [
  { value: 'bottom-left', label: 'From Bottom-left' },
  { value: 'bottom-right', label: 'From Bottom-right' },
  { value: 'top-left', label: 'From Top-left' },
  { value: 'top-right', label: 'From Top-right' },
];

const CORNER_POS: Record<AlertCorner, string> = {
  'bottom-left': 'bottom-2 left-2',
  'bottom-right': 'bottom-2 right-2',
  'top-left': 'left-2 top-7',
  'top-right': 'right-2 top-7',
};

function AlertMock({ corner }: { corner: AlertCorner }) {
  return (
    <div className="border-border bg-surface-subtle relative h-28 w-full overflow-hidden rounded-xl border">
      <div className="border-border bg-surface flex h-5 items-center gap-1 border-b px-2">
        <span className="bg-border-strong/30 h-1.5 w-1.5 rounded-full" />
        <span className="bg-border-strong/20 h-1.5 w-8 rounded-full" />
      </div>
      <div className="border-border bg-surface absolute bottom-0 left-0 top-5 w-9 space-y-1.5 border-r p-1.5">
        <span className="bg-border-strong/15 block h-1.5 w-full rounded-full" />
        <span className="bg-border-strong/15 block h-1.5 w-full rounded-full" />
        <span className="bg-border-strong/15 block h-1.5 w-2/3 rounded-full" />
      </div>
      <div className={`absolute ${CORNER_POS[corner]} bg-ink/85 h-9 w-16 rounded-lg shadow-sm`} />
    </div>
  );
}

export function CustomAlertPage() {
  const [location, setLocation] = useState<AlertCorner>('bottom-left');
  const [showAdd, setShowAdd] = useState(false);
  const [editingAlert, setEditingAlert] = useState<any>(null);
  const queryClient = useQueryClient();

  const { data: popups = [], isLoading } = useQuery({
    queryKey: ['popups'],
    queryFn: () => api.marketing.listPopups(),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => api.marketing.createPopup(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['popups'] });
      setShowAdd(false);
      setEditingAlert(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; payload: any }) =>
      api.marketing.updatePopup(data.id, data.payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['popups'] });
      setShowAdd(false);
      setEditingAlert(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.marketing.deletePopup(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['popups'] });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const payload = {
      title: fd.get('title') as string,
      imageUrl: (fd.get('imageUrl') as string) || null,
      linkUrl: (fd.get('linkUrl') as string) || null,
      location: fd.get('location') as string,
      delaySeconds: parseInt(fd.get('delaySeconds') as string, 10),
      startsAt: fd.get('startsAt') ? new Date(fd.get('startsAt') as string).toISOString() : null,
      endsAt: fd.get('endsAt') ? new Date(fd.get('endsAt') as string).toISOString() : null,
      active: fd.get('active') === 'on',
    };
    if (editingAlert) {
      updateMutation.mutate({ id: editingAlert.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const currentAlerts = popups.filter((p) => p.location === location);

  return (
    <div className="space-y-6 pb-8">
      {/* Location picker */}
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm">
        <h3 className="text-ink mb-1 text-[15px] font-extrabold">Select Alert Location</h3>
        <p className="text-ink-muted mb-5 text-[13px] font-medium">
          Pick where on the page the alert will appear.
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {ALERT_LOCATIONS.map((loc) => {
            const selected = location === loc.value;
            return (
              <button
                key={loc.value}
                type="button"
                onClick={() => setLocation(loc.value)}
                className={`group rounded-2xl border p-3 text-left transition-all duration-200 ${
                  selected
                    ? 'border-ink bg-surface-subtle ring-ink/20 ring-2'
                    : 'border-border/60 bg-surface-subtle/20 hover:border-ink'
                }`}
              >
                <AlertMock corner={loc.value} />
                <p className={`mt-3 text-[13px] font-bold ${selected ? 'text-ink' : 'text-ink'}`}>
                  {loc.label}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Add form */}
      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm"
          >
            <div className="border-border/40 mb-5 flex items-center gap-2 border-b pb-4">
              <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
                <Megaphone className="h-4 w-4" />
              </div>
              <h3 className="text-ink text-[15px] font-extrabold">
                {editingAlert ? 'Edit Alert' : 'New Custom Alert'}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <input type="hidden" name="location" value={location} />
              <div className="lg:col-span-2">
                <FieldLabel>Alert Title</FieldLabel>
                <Input
                  name="title"
                  required
                  placeholder="e.g. Flash Sale Live!"
                  defaultValue={editingAlert?.title}
                />
              </div>
              <div>
                <FieldLabel>Delay (seconds)</FieldLabel>
                <Input
                  name="delaySeconds"
                  type="number"
                  min="0"
                  required
                  defaultValue={editingAlert?.delaySeconds || 0}
                />
              </div>
              <div>
                <FieldLabel>Image URL</FieldLabel>
                <Input
                  name="imageUrl"
                  placeholder="https://..."
                  defaultValue={editingAlert?.imageUrl}
                />
              </div>
              <div>
                <FieldLabel>Link URL</FieldLabel>
                <Input
                  name="linkUrl"
                  placeholder="https://..."
                  defaultValue={editingAlert?.linkUrl}
                />
              </div>
              <div>
                <FieldLabel>Starts At (optional)</FieldLabel>
                <Input
                  name="startsAt"
                  type="datetime-local"
                  defaultValue={editingAlert?.startsAt?.slice(0, 16)}
                />
              </div>
              <div>
                <FieldLabel>Ends At (optional)</FieldLabel>
                <Input
                  name="endsAt"
                  type="datetime-local"
                  defaultValue={editingAlert?.endsAt?.slice(0, 16)}
                />
              </div>
              <div className="flex flex-col justify-between gap-3">
                <label className="mt-5 flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    name="active"
                    defaultChecked={editingAlert ? editingAlert.active : true}
                    className="border-border/60 rounded"
                  />
                  <span className="text-ink text-[13px] font-bold">Active</span>
                </label>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="bg-ink hover:bg-ink/90 flex h-10 w-full items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : 'Save Alert'}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Alerts Table */}
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
              <Megaphone className="h-4 w-4" strokeWidth={2} />
            </div>
            <span className="text-ink text-[14px] font-bold">
              Alerts — {ALERT_LOCATIONS.find((l) => l.value === location)?.label}
            </span>
          </div>
          <button
            onClick={() => {
              setEditingAlert(null);
              setShowAdd((v) => !v);
            }}
            className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            {showAdd ? 'Close Form' : '+ Create Alert'}
          </button>
        </div>
        <div className="p-0">
          {isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : currentAlerts.length === 0 ? (
            <div className="border-border/60 bg-surface-subtle/30 m-5 rounded-2xl border border-dashed py-12">
              <EmptyState
                icon={<Megaphone className="text-ink-muted h-10 w-10" />}
                title="No alerts"
                description={`No custom alerts for ${location} yet.`}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-16 py-3 pl-5 pr-4">#</th>
                    <th className="py-3 pr-4">Title</th>
                    <th className="py-3 pr-4 text-center">Delay</th>
                    <th className="py-3 pr-4 text-center">Status</th>
                    <th className="py-3 pr-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {currentAlerts.map((alert: any, idx: number) => (
                    <tr key={alert.id} className="hover:bg-surface-subtle/30 transition-colors">
                      <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                        {(idx + 1).toString().padStart(2, '0')}
                      </td>
                      <td className="text-ink py-3 pr-4 font-semibold">{alert.title}</td>
                      <td className="py-3 pr-4 text-center">
                        <span className="bg-surface-subtle text-ink-muted border-border/40 inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-[12px] font-bold">
                          {alert.delaySeconds}s
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-center">
                        <Badge tone={alert.active ? 'success' : 'neutral'}>
                          {alert.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-3 pr-5">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => {
                              setEditingAlert(alert);
                              setShowAdd(true);
                            }}
                            className="text-ink-muted hover:text-ink hover:bg-surface-subtle flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                          >
                            <Edit2 className="h-[16px] w-[16px]" strokeWidth={2} />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Delete alert "${alert.title}"?`))
                                deleteMutation.mutate(alert.id);
                            }}
                            className="text-ink-muted flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-[18px] w-[18px]" strokeWidth={2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Flash deals & newsletters ──────────────────────────────────────

export { FlashDealsPage } from './FlashDealsPage';
export { SellerOffersPage } from './SellerOffersPage';

export function EmailsNewslettersPage() {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editingNewsletter, setEditingNewsletter] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'NEWSLETTER' | 'TEMPLATE'>('NEWSLETTER');

  const { data: newslettersResponse, isLoading } = useQuery({
    queryKey: ['newsletters', activeTab],
    queryFn: () => api.marketing.listNewsletters({ kind: activeTab }),
  });

  const newsletters = newslettersResponse || [];

  const createMutation = useMutation({
    mutationFn: (data: any) => api.marketing.createNewsletter(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['newsletters', activeTab] });
      setShowAdd(false);
      setEditingNewsletter(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; payload: any }) =>
      api.marketing.updateNewsletter(data.id, data.payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['newsletters', activeTab] });
      setShowAdd(false);
      setEditingNewsletter(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.marketing.deleteNewsletter(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['newsletters', activeTab] });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const payload = {
      subject: fd.get('subject') as string,
      kind: activeTab,
      bodyHtml: fd.get('bodyHtml') as string,
      status: fd.get('status') as string,
      scheduledAt: fd.get('scheduledAt')
        ? new Date(fd.get('scheduledAt') as string).toISOString()
        : null,
    };
    if (editingNewsletter) {
      updateMutation.mutate({ id: editingNewsletter.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <div className="space-y-6 pb-8">
      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm"
          >
            <div className="border-border/40 mb-5 flex items-center gap-2 border-b pb-4">
              <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
                <Mail className="h-4 w-4" />
              </div>
              <h3 className="text-ink text-[15px] font-extrabold">
                {editingNewsletter
                  ? `Edit ${activeTab === 'TEMPLATE' ? 'Template' : 'Newsletter'}`
                  : `New ${activeTab === 'TEMPLATE' ? 'Template' : 'Newsletter'}`}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <FieldLabel>Subject Line</FieldLabel>
                <Input
                  name="subject"
                  required
                  placeholder="e.g. Big Summer Sale is Here!"
                  defaultValue={editingNewsletter?.subject}
                />
              </div>
              <div>
                <FieldLabel>Status</FieldLabel>
                <FormSelect name="status" defaultValue={editingNewsletter?.status || 'DRAFT'}>
                  <option value="DRAFT">Draft</option>
                  <option value="SCHEDULED">Scheduled</option>
                  <option value="SENT">Sent</option>
                </FormSelect>
              </div>
              <div className="lg:col-span-3">
                <FieldLabel>HTML Body</FieldLabel>
                <textarea
                  name="bodyHtml"
                  required
                  rows={6}
                  placeholder="<h1>Hello, world!</h1>"
                  defaultValue={editingNewsletter?.bodyHtml}
                  className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 w-full rounded-xl border p-3 text-[13px] outline-none transition-colors focus:ring-2"
                />
              </div>
              <div>
                <FieldLabel>Scheduled At (optional)</FieldLabel>
                <Input
                  name="scheduledAt"
                  type="datetime-local"
                  defaultValue={editingNewsletter?.scheduledAt?.slice(0, 16)}
                />
              </div>
              <div className="flex items-end justify-end lg:col-span-2">
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="bg-ink hover:bg-ink/90 flex h-10 w-full items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all disabled:opacity-50 lg:w-48"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : 'Save Newsletter'}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
              <Mail className="h-4 w-4" strokeWidth={2} />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setActiveTab('NEWSLETTER');
                  setShowAdd(false);
                  setEditingNewsletter(null);
                }}
                className={`rounded-lg px-3 py-1.5 text-[13px] font-bold transition-colors ${activeTab === 'NEWSLETTER' ? 'bg-ink text-white shadow-sm' : 'text-ink-muted hover:bg-surface hover:text-ink'}`}
              >
                Newsletters
              </button>
              <button
                onClick={() => {
                  setActiveTab('TEMPLATE');
                  setShowAdd(false);
                  setEditingNewsletter(null);
                }}
                className={`rounded-lg px-3 py-1.5 text-[13px] font-bold transition-colors ${activeTab === 'TEMPLATE' ? 'bg-ink text-white shadow-sm' : 'text-ink-muted hover:bg-surface hover:text-ink'}`}
              >
                Email Templates
              </button>
            </div>
          </div>
          <button
            onClick={() => {
              setEditingNewsletter(null);
              setShowAdd((v) => !v);
            }}
            className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            {showAdd
              ? 'Close Form'
              : `+ Create ${activeTab === 'TEMPLATE' ? 'Template' : 'Newsletter'}`}
          </button>
        </div>
        <div className="p-0">
          {isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : newsletters.length === 0 ? (
            <div className="border-border/60 bg-surface-subtle/30 m-5 rounded-2xl border border-dashed py-12">
              <EmptyState
                icon={<Mail className="text-ink-muted h-10 w-10" />}
                title={`No ${activeTab === 'TEMPLATE' ? 'templates' : 'newsletters'}`}
                description={`Create a ${activeTab === 'TEMPLATE' ? 'template' : 'newsletter'} to engage with your subscribers.`}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-16 py-3 pl-5 pr-4">#</th>
                    <th className="py-3 pr-4">Subject</th>
                    <th className="py-3 pr-4 text-center">Status</th>
                    <th className="py-3 pr-4 text-center">Scheduled</th>
                    <th className="py-3 pr-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {newsletters.map((nl: any, idx: number) => (
                    <tr key={nl.id} className="hover:bg-surface-subtle/30 transition-colors">
                      <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                        {(idx + 1).toString().padStart(2, '0')}
                      </td>
                      <td className="text-ink py-3 pr-4 font-semibold">{nl.subject}</td>
                      <td className="py-3 pr-4 text-center">
                        <Badge
                          tone={
                            nl.status === 'SENT'
                              ? 'success'
                              : nl.status === 'SCHEDULED'
                                ? 'warning'
                                : 'neutral'
                          }
                        >
                          {nl.status}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4 text-center">
                        {nl.scheduledAt ? new Date(nl.scheduledAt).toLocaleString() : '-'}
                      </td>
                      <td className="py-3 pr-5">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => {
                              setEditingNewsletter(nl);
                              setShowAdd(true);
                            }}
                            className="text-ink-muted hover:text-ink hover:bg-surface-subtle flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                          >
                            <Edit2 className="h-[16px] w-[16px]" strokeWidth={2} />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Delete newsletter "${nl.subject}"?`))
                                deleteMutation.mutate(nl.id);
                            }}
                            className="text-ink-muted flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-[18px] w-[18px]" strokeWidth={2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function SubscribersPage() {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editingSub, setEditingSub] = useState<any>(null);

  const { data: subscribersResponse, isLoading } = useQuery({
    queryKey: ['subscribers'],
    queryFn: () => api.marketing.listSubscribers(),
  });

  const subscribers = subscribersResponse || [];

  const createMutation = useMutation({
    mutationFn: (data: any) => api.marketing.createSubscriber(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['subscribers'] });
      setShowAdd(false);
      setEditingSub(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; payload: any }) =>
      api.marketing.updateSubscriber(data.id, data.payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['subscribers'] });
      setShowAdd(false);
      setEditingSub(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.marketing.deleteSubscriber(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['subscribers'] });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const payload = {
      email: fd.get('email') as string,
      name: (fd.get('name') as string) || null,
      source: (fd.get('source') as string) || null,
      active: fd.get('active') === 'on',
    };
    if (editingSub) {
      updateMutation.mutate({ id: editingSub.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <div className="space-y-6 pb-8">
      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm"
          >
            <div className="border-border/40 mb-5 flex items-center gap-2 border-b pb-4">
              <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
                <Users className="h-4 w-4" />
              </div>
              <h3 className="text-ink text-[15px] font-extrabold">
                {editingSub ? 'Edit Subscriber' : 'New Subscriber'}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <FieldLabel>Email</FieldLabel>
                <Input
                  name="email"
                  type="email"
                  required
                  placeholder="user@example.com"
                  defaultValue={editingSub?.email}
                />
              </div>
              <div>
                <FieldLabel>Name (optional)</FieldLabel>
                <Input name="name" placeholder="John Doe" defaultValue={editingSub?.name} />
              </div>
              <div>
                <FieldLabel>Source</FieldLabel>
                <Input name="source" placeholder="e.g. footer" defaultValue={editingSub?.source} />
              </div>
              <div className="flex flex-col justify-between gap-3 lg:col-span-3">
                <label className="mt-2 flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    name="active"
                    defaultChecked={editingSub ? editingSub.active : true}
                    className="border-border/60 rounded"
                  />
                  <span className="text-ink text-[13px] font-bold">Active (Subscribed)</span>
                </label>
                <div className="mt-4 flex justify-end">
                  <button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="bg-ink hover:bg-ink/90 flex h-10 w-48 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all disabled:opacity-50"
                  >
                    {createMutation.isPending || updateMutation.isPending
                      ? 'Saving...'
                      : 'Save Subscriber'}
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
              <Users className="h-4 w-4" strokeWidth={2} />
            </div>
            <span className="text-ink text-[14px] font-bold">Subscribers</span>
          </div>
          <button
            onClick={() => {
              setEditingSub(null);
              setShowAdd((v) => !v);
            }}
            className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            {showAdd ? 'Close Form' : '+ Add Subscriber'}
          </button>
        </div>
        <div className="p-0">
          {isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : subscribers.length === 0 ? (
            <div className="border-border/60 bg-surface-subtle/30 m-5 rounded-2xl border border-dashed py-12">
              <EmptyState
                icon={<Users className="text-ink-muted h-10 w-10" />}
                title="No subscribers"
                description="You don't have any subscribers yet."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-16 py-3 pl-5 pr-4">#</th>
                    <th className="py-3 pr-4">Email</th>
                    <th className="py-3 pr-4">Name</th>
                    <th className="py-3 pr-4 text-center">Status</th>
                    <th className="py-3 pr-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {subscribers.map((sub: any, idx: number) => (
                    <tr key={sub.id} className="hover:bg-surface-subtle/30 transition-colors">
                      <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                        {(idx + 1).toString().padStart(2, '0')}
                      </td>
                      <td className="text-ink py-3 pr-4 font-semibold">{sub.email}</td>
                      <td className="text-ink py-3 pr-4">{sub.name || '-'}</td>
                      <td className="py-3 pr-4 text-center">
                        <Badge tone={sub.active ? 'success' : 'neutral'}>
                          {sub.active ? 'Subscribed' : 'Unsubscribed'}
                        </Badge>
                      </td>
                      <td className="py-3 pr-5">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => {
                              setEditingSub(sub);
                              setShowAdd(true);
                            }}
                            className="text-ink-muted hover:text-ink hover:bg-surface-subtle flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                          >
                            <Edit2 className="h-[16px] w-[16px]" strokeWidth={2} />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Delete subscriber "${sub.email}"?`))
                                deleteMutation.mutate(sub.id);
                            }}
                            className="text-ink-muted flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-[18px] w-[18px]" strokeWidth={2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function CouponsPage() {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<any>(null);

  const { data: coupons = [], isLoading } = useQuery({
    queryKey: ['coupons'],
    queryFn: () => api.marketing.listCoupons(),
  });

  const createMutation = useMutation({
    mutationFn: (payload: any) => api.marketing.createCoupon(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coupons'] });
      setShowAdd(false);
      setEditingCoupon(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      api.marketing.updateCoupon(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coupons'] });
      setShowAdd(false);
      setEditingCoupon(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.marketing.deleteCoupon(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['coupons'] }),
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const toPaise = (name: string) => Math.round((Number(formData.get(name)) || 0) * 100);
    const optionalNumber = (name: string) => {
      const raw = String(formData.get(name) || '').trim();
      return raw ? Number(raw) : null;
    };
    const payload = {
      code: String(formData.get('code') || '')
        .trim()
        .toUpperCase(),
      title: String(formData.get('title') || '').trim() || null,
      discountType: formData.get('discountType') as 'PERCENTAGE' | 'FLAT',
      discountAmount:
        formData.get('discountType') === 'PERCENTAGE'
          ? Number(formData.get('discountAmount')) || 0
          : toPaise('discountAmount'),
      active: formData.get('active') === 'on',
      startsAt: toIso(formData.get('startsAt')),
      endsAt: toIso(formData.get('endsAt')),
      minSubtotalPaise: toPaise('minSubtotal'),
      maxDiscountPaise: optionalMoneyToPaise(formData.get('maxDiscount')),
      usageLimit: optionalNumber('usageLimit'),
      perUserLimit: optionalNumber('perUserLimit'),
    };

    if (editingCoupon) updateMutation.mutate({ id: editingCoupon.id, payload });
    else createMutation.mutate(payload);
  };

  return (
    <div className="space-y-6 pb-8">
      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm"
          >
            <div className="border-border/40 mb-5 flex items-center gap-2 border-b pb-4">
              <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
                <Percent className="h-4 w-4" />
              </div>
              <h3 className="text-ink text-[15px] font-extrabold">
                {editingCoupon ? 'Edit Coupon' : 'New Coupon'}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <FieldLabel>Code</FieldLabel>
                <Input
                  name="code"
                  required
                  placeholder="WELCOME10"
                  defaultValue={editingCoupon?.code}
                />
              </div>
              <div>
                <FieldLabel>Title</FieldLabel>
                <Input
                  name="title"
                  placeholder="Welcome offer"
                  defaultValue={editingCoupon?.title}
                />
              </div>
              <div>
                <FieldLabel>Discount Type</FieldLabel>
                <FormSelect
                  name="discountType"
                  defaultValue={editingCoupon?.discountType || 'PERCENTAGE'}
                >
                  <option value="PERCENTAGE">Percentage</option>
                  <option value="FLAT">Flat amount</option>
                </FormSelect>
              </div>
              <div>
                <FieldLabel>Discount</FieldLabel>
                <Input
                  name="discountAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  defaultValue={couponAmountInput(editingCoupon)}
                />
              </div>
              <div>
                <FieldLabel>Min Subtotal</FieldLabel>
                <Input
                  name="minSubtotal"
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue={paiseInput(editingCoupon?.minSubtotalPaise)}
                />
              </div>
              <div>
                <FieldLabel>Max Discount</FieldLabel>
                <Input
                  name="maxDiscount"
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue={paiseInput(editingCoupon?.maxDiscountPaise)}
                />
              </div>
              <div>
                <FieldLabel>Total Usage Limit</FieldLabel>
                <Input
                  name="usageLimit"
                  type="number"
                  min="1"
                  defaultValue={editingCoupon?.usageLimit ?? ''}
                />
              </div>
              <div>
                <FieldLabel>Per User Limit</FieldLabel>
                <Input
                  name="perUserLimit"
                  type="number"
                  min="1"
                  defaultValue={editingCoupon?.perUserLimit ?? ''}
                />
              </div>
              <div>
                <FieldLabel>Starts At</FieldLabel>
                <Input
                  name="startsAt"
                  type="datetime-local"
                  defaultValue={dateTimeLocal(editingCoupon?.startsAt)}
                />
              </div>
              <div>
                <FieldLabel>Ends At</FieldLabel>
                <Input
                  name="endsAt"
                  type="datetime-local"
                  defaultValue={dateTimeLocal(editingCoupon?.endsAt)}
                />
              </div>
              <label className="flex items-end gap-2 pb-2">
                <input
                  type="checkbox"
                  name="active"
                  defaultChecked={editingCoupon ? editingCoupon.active : true}
                  className="border-border/60 rounded"
                />
                <span className="text-ink text-[13px] font-bold">Active</span>
              </label>
              <div className="flex items-end justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAdd(false);
                    setEditingCoupon(null);
                  }}
                  className="border-border/60 text-ink-muted hover:bg-surface-subtle h-10 rounded-xl border px-4 text-[13px] font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="bg-ink hover:bg-ink/90 h-10 rounded-xl px-4 text-[13px] font-bold text-white shadow-sm disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : 'Save Coupon'}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
              <Percent className="h-4 w-4" strokeWidth={2} />
            </div>
            <span className="text-ink text-[14px] font-bold">Coupons</span>
          </div>
          <button
            onClick={() => {
              setEditingCoupon(null);
              setShowAdd((v) => !v);
            }}
            className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            {showAdd ? 'Close Form' : '+ Add Coupon'}
          </button>
        </div>
        {isLoading ? (
          <div className="grid min-h-[300px] place-items-center">
            <Spinner size="lg" />
          </div>
        ) : coupons.length === 0 ? (
          <div className="border-border/60 bg-surface-subtle/30 m-5 rounded-2xl border border-dashed py-12">
            <EmptyState
              icon={<Percent className="text-ink-muted h-10 w-10" />}
              title="No coupons"
              description="Create coupons for cart-level promotional discounts."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                  <th className="py-3 pl-5 pr-4">Code</th>
                  <th className="py-3 pr-4">Discount</th>
                  <th className="py-3 pr-4">Limits</th>
                  <th className="py-3 pr-4">Window</th>
                  <th className="py-3 pr-4 text-center">Status</th>
                  <th className="py-3 pr-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y text-[13px]">
                {coupons.map((coupon: any) => (
                  <tr key={coupon.id} className="hover:bg-surface-subtle/30 transition-colors">
                    <td className="py-3 pl-5 pr-4">
                      <div className="text-ink font-mono text-[13px] font-extrabold">
                        {coupon.code}
                      </div>
                      <div className="text-ink-muted text-[11px]">{coupon.title || '-'}</div>
                    </td>
                    <td className="text-ink py-3 pr-4 font-semibold">
                      {couponDiscountLabel(coupon)}
                    </td>
                    <td className="text-ink-muted py-3 pr-4 text-[12px]">
                      Min {money(coupon.minSubtotalPaise)} / Used {coupon.usedCount}
                      {coupon.usageLimit ? ` of ${coupon.usageLimit}` : ''}
                    </td>
                    <td className="text-ink-muted py-3 pr-4 text-[12px]">
                      {shortDate(coupon.startsAt)} - {shortDate(coupon.endsAt)}
                    </td>
                    <td className="py-3 pr-4 text-center">
                      <Badge tone={coupon.active ? 'success' : 'neutral'}>
                        {coupon.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="py-3 pr-5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => {
                            setEditingCoupon(coupon);
                            setShowAdd(true);
                          }}
                          className="text-ink-muted hover:text-ink hover:bg-surface-subtle flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                        >
                          <Edit2 className="h-[16px] w-[16px]" strokeWidth={2} />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete coupon "${coupon.code}"?`))
                              deleteMutation.mutate(coupon.id);
                          }}
                          className="text-ink-muted flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-[18px] w-[18px]" strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function toIso(value: FormDataEntryValue | null) {
  const text = String(value || '').trim();
  return text ? new Date(text).toISOString() : null;
}

function optionalMoneyToPaise(value: FormDataEntryValue | null) {
  const text = String(value || '').trim();
  return text ? Math.round(Number(text) * 100) : null;
}

function paiseInput(value?: number | null) {
  return value ? (value / 100).toFixed(2) : '';
}

function couponAmountInput(coupon: any) {
  if (!coupon) return '';
  return coupon.discountType === 'FLAT' ? paiseInput(coupon.discountAmount) : coupon.discountAmount;
}

function dateTimeLocal(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
}

function money(value?: number | null) {
  return `₹${((value ?? 0) / 100).toFixed(2)}`;
}

function shortDate(value?: string | null) {
  if (!value) return 'Open';
  return new Date(value).toLocaleDateString();
}

function couponDiscountLabel(coupon: any) {
  if (coupon.discountType === 'PERCENTAGE') {
    return `${coupon.discountAmount}%${coupon.maxDiscountPaise ? ` up to ${money(coupon.maxDiscountPaise)}` : ''}`;
  }
  return money(coupon.discountAmount);
}
